import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PermissionOutput } from '../../services/models/permission.model';
import { PermissionNodeComponent } from './permission-node/permission-node.component';

export interface PermissionNode {
  permission: PermissionOutput;
  selected: boolean;
  indeterminate: boolean;
  expanded: boolean;
  children: PermissionNode[];
}

@Component({
  selector: 'app-permission-tree',
  standalone: true,
  imports: [CommonModule, FormsModule, PermissionNodeComponent],
  templateUrl: './permission-tree.component.html',
  styleUrl: './permission-tree.component.css'
})
export class PermissionTreeComponent implements OnInit, OnChanges {
  @Input() permissions: PermissionOutput[] = [];
  @Input() selectedKeys: string[] = [];
  @Output() selectedKeysChange = new EventEmitter<string[]>();

  nodes: PermissionNode[] = [];
  private allSelectedKeys: Set<string> = new Set();

  ngOnInit(): void {
    this.buildTree();
    if (this.selectedKeys.length > 0) {
      this.allSelectedKeys = new Set(this.selectedKeys);
      this.updateSelectionsFromKeys();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['permissions'] && this.permissions && this.permissions.length > 0) {
      this.buildTree();
      if (this.selectedKeys.length > 0) {
        this.allSelectedKeys = new Set(this.selectedKeys);
        this.updateSelectionsFromKeys();
      }
    }
    if (changes['selectedKeys'] && this.selectedKeys) {
      this.allSelectedKeys = new Set(this.selectedKeys);
      this.updateSelectionsFromKeys();
    }
  }

  private buildTree(): void {
    this.nodes = this.permissions.map(p => this.buildNode(p));
  }

  private buildNode(permission: PermissionOutput): PermissionNode {
    return {
      permission,
      selected: false,
      indeterminate: false,
      expanded: true,
      children: permission.children.map(child => this.buildNode(child))
    };
  }

  private updateSelectionsFromKeys(): void {
    this.nodes.forEach(node => {
      this.updateNodeSelection(node);
    });
  }

  private updateNodeSelection(node: PermissionNode): void {
    const isSelected = this.allSelectedKeys.has(node.permission.key);
    const hasSelectedChildren = node.children.some(child => this.allSelectedKeys.has(child.permission.key));

    node.selected = isSelected;
    node.indeterminate = !isSelected && hasSelectedChildren;

    // Atualizar filhos recursivamente
    node.children.forEach(child => {
      this.updateNodeSelection(child);
    });
  }

  toggleExpand(node: PermissionNode): void {
    node.expanded = !node.expanded;
  }

  toggleSelection(node: PermissionNode): void {
    node.selected = !node.selected;
    this.updateSelection(node, node.selected);
    this.emitSelection();
  }

  private updateSelection(node: PermissionNode, selected: boolean): void {
    if (selected) {
      this.allSelectedKeys.add(node.permission.key);
      // Selecionar todos os filhos
      node.children.forEach(child => {
        this.updateSelection(child, true);
      });
    } else {
      this.allSelectedKeys.delete(node.permission.key);
      // Desselecionar todos os filhos
      node.children.forEach(child => {
        this.updateSelection(child, false);
      });
    }

    // Atualizar estado dos nós
    this.updateNodeStates();
  }

  private updateNodeStates(): void {
    this.nodes.forEach(node => {
      this.updateNodeState(node);
    });
  }

  private updateNodeState(node: PermissionNode): void {
    const isSelected = this.allSelectedKeys.has(node.permission.key);
    const allChildrenSelected = node.children.length > 0 && 
      node.children.every(child => this.allSelectedKeys.has(child.permission.key));
    const someChildrenSelected = node.children.length > 0 && 
      node.children.some(child => this.allSelectedKeys.has(child.permission.key));

    node.selected = isSelected || allChildrenSelected;
    node.indeterminate = !node.selected && someChildrenSelected;

    // Atualizar filhos recursivamente
    node.children.forEach(child => {
      this.updateNodeState(child);
    });
  }

  private emitSelection(): void {
    const keys = Array.from(this.allSelectedKeys);
    this.selectedKeysChange.emit(keys);
  }

  getSelectedCount(): number {
    return this.allSelectedKeys.size;
  }
}
